'use client';

import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { X } from 'lucide-react';

import { Backdrop } from './backdrop';
import { useEscapeDismiss } from './dismiss-stack';
import { cn } from '../utils/cn';

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: ReactNode;
	ariaLabel?: string;
	className?: string;
	showCloseButton?: boolean;
	closeOnOverlayClick?: boolean;
	closeOnEscape?: boolean;
	/**
	 * Keep keyboard focus inside the modal while open and restore it to the
	 * previously-focused element on close. Opt-in (default false) so existing
	 * modals are unchanged; required for blocking/mandatory dialogs where Tab
	 * must not reach the app behind the scrim.
	 */
	trapFocus?: boolean;
}

export const Modal = ({
	isOpen,
	onClose,
	children,
	ariaLabel,
	className,
	showCloseButton = true,
	closeOnOverlayClick = true,
	closeOnEscape = true,
	trapFocus = false
}: ModalProps) => {
	useEscapeDismiss(isOpen && closeOnEscape, onClose);
	const contentRef = useRef<HTMLDivElement>(null);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	// Move focus into the modal on open (respecting any child autoFocus) and
	// restore it to the previously-focused element on close.
	useEffect(() => {
		if (!isOpen || !trapFocus) return;
		const previouslyFocused = document.activeElement as HTMLElement | null;
		const content = contentRef.current;
		if (content && !content.contains(document.activeElement)) {
			const focusables = content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
			(focusables[0] ?? content).focus();
		}
		return () => previouslyFocused?.focus?.();
	}, [isOpen, trapFocus]);

	const handleContentKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (!trapFocus || e.key !== 'Tab') return;
		const content = contentRef.current;
		if (!content) return;
		const focusables = Array.from(
			content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
		).filter((el) => el.offsetParent !== null || el === document.activeElement);
		if (focusables.length === 0) {
			e.preventDefault();
			return;
		}
		const first = focusables[0];
		const last = focusables[focusables.length - 1];
		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	if (!isOpen || typeof document === 'undefined') return null;

	return createPortal(
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center"
			role="dialog"
			aria-label={ariaLabel}
			aria-modal="true">
			{/* Overlay */}
			<Backdrop variant="dialog" onClick={closeOnOverlayClick ? onClose : undefined} />

			{/* Content */}
			<div
				ref={contentRef}
				tabIndex={-1}
				onKeyDown={handleContentKeyDown}
				className={cn(
					'relative z-10 max-h-[90vh] overflow-hidden outline-none',
					'bg-dialog backdrop-blur-[16px] rounded-2xl',
					'border border-border-weak shadow-dialog',
					'animate-in fade-in-0 zoom-in-95 duration-200',
					className
				)}
				onClick={(e) => e.stopPropagation()}>
				{showCloseButton && (
					<button
						onClick={onClose}
						className="absolute top-4 right-4 p-1 text-neutral-muted hover:text-neutral transition-colors bg-transparent border-none cursor-pointer z-10"
						aria-label="Close">
						<X className="w-5 h-5" strokeWidth={2} />
					</button>
				)}
				{children}
			</div>
		</div>,
		document.body
	);
};
